import { useGraphQLMutation } from '@opengov/bnp-core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  InsertNavigationMenuItemForOnlinePublicationMutation,
  InsertNavigationMenuItemForOnlinePublicationMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { getUserById } from '../../../../routes/hasura';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

const InsertNavigationMenuItemForOnlinePublicationDocument = graphql(`
  mutation InsertNavigationMenuItemForOnlinePublication(
    $object: publicationNavigationMenuItems_insert_input!
  ) {
    insert_publicationNavigationMenuItems_one(object: $object) {
      id
    }
  }
`);

export type UseInsertNavigationMenuMessages = {
  successTitle: string;
  // `successBody` receives the menu title via callback so the caller can
  // format the locale string (e.g. `${title} added`).
  successBody: (params: { name: string }) => string;
  errorTitle: string;
  errorBody?: string;
};

export type InsertNavigationMenuParams = {
  publicationId: string;
  title: string;
  userId: number;
  order: number;
};

export function useInsertNavigationMenu(messages: UseInsertNavigationMenuMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const mutation = useGraphQLMutation<
    InsertNavigationMenuItemForOnlinePublicationMutation,
    InsertNavigationMenuItemForOnlinePublicationMutationVariables
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'insertNavigationMenu'],
    document: InsertNavigationMenuItemForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL
  });

  const insertNavigationMenu = useCallback(
    async (
      params: InsertNavigationMenuParams
    ): Promise<{ menuId: string; publicationId: string } | null> => {
      const { publicationId, title, userId, order } = params;
      try {
        // `getUserById` resolves the user's opengovId-derived author id used
        // throughout stories-app for createdBy/updatedBy columns.
        const createdBy = await getUserById(userId);
        const result = await mutation.mutateAsync({
          object: { publicationId, createdBy, title, order }
        });
        const menuId = result?.insert_publicationNavigationMenuItems_one?.id;
        if (!menuId) {
          throw new Error('Insert returned no id');
        }
        await queryClient.invalidateQueries({
          queryKey: setupOnlinePublicationQueryKeys.navigationMenus({ publicationId })
        });
        toggleToast({
          title: messages.successTitle,
          body: messages.successBody({ name: title }),
          severity: 'success'
        });
        return { menuId, publicationId };
      } catch {
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        });
        return null;
      }
    },
    [mutation, queryClient, messages, toggleToast]
  );

  return {
    insertNavigationMenu,
    isLoading: mutation.isLoading
  };
}
